/**
 * Data Hub — Staging Records tab.
 *
 * Bucket filter, search field, and search — one toolbar row matching
 * Production records. Sortable common Table in a white card.
 */
import { useMemo, useState } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import DropdownSelector from "../../Components/DropdownSelector";
import SearchInput from "../../Components/Search/SearchInput";
import Table from "../../Components/Table";
import { TabError, TabLoading } from "../components/TabState";
import { DATASET_LABELS } from "../data/dataset";
import {
  BUCKET_FILTERS,
  type BucketFilter,
  type StagingRow,
} from "../data/types";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { useAdapterData } from "../hooks/useAdapterData";
import { useDataHubAdapter } from "../hooks/useDataHubAdapter";

type SearchField =
  | "date"
  | "account"
  | "metric"
  | "category"
  | "state"
  | "bucket"
  | "status";

const SEARCH_FIELDS: { value: SearchField; label: string }[] = [
  { value: "account", label: "Account" },
  { value: "date", label: "Date" },
  { value: "metric", label: "Metric" },
  { value: "category", label: "Metric Category" },
  { value: "state", label: "State" },
  { value: "bucket", label: "Bucket" },
  { value: "status", label: "Status" },
];

type SortKey =
  | "account"
  | "metric_id"
  | "metric_name"
  | "date"
  | "value"
  | "lifecycle"
  | "state"
  | "bucket"
  | "status";

type SortState = { key: string; order: "asc" | "desc" } | null;

const BUCKET_LABELS: Record<BucketFilter, string> = {
  all: "All",
  open: "Open",
  needs_review: "Needs review",
  fixed: "Fixed",
  deduped: "Deduped",
  ignored: "Ignored",
  promoted: "Promoted",
  rejected: "Rejected",
};

const BUCKET_PILL: Record<string, string> = {
  open: "bg-amber-100 text-amber-900",
  fixed: "bg-blue-100 text-blue-800",
  deduped: "bg-purple-100 text-purple-800",
  ignored: "bg-gray-100 text-gray-600",
  promoted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  clean: "bg-green-50 text-green-800",
};

function fieldValue(row: StagingRow, field: SearchField): string {
  switch (field) {
    case "date":
      return row.measurement_date ?? "";
    case "account":
      return row.account_name ?? "";
    case "metric":
      return `${row.metric_id ?? ""} ${row.metric_name ?? ""}`;
    case "category":
      return row.category ?? "";
    case "state":
      return row.stage_state;
    case "bucket":
      return row.qc?.bucket ?? "";
    case "status":
      return row.pipeline_status ?? "";
    default:
      return "";
  }
}

function sortValue(row: StagingRow, key: SortKey): string | number {
  switch (key) {
    case "account":
      return row.account_name ?? "";
    case "metric_id":
      return row.metric_id ?? "";
    case "metric_name":
      return row.metric_name ?? "";
    case "date":
      return row.measurement_date ?? "";
    case "value":
      return typeof row.value === "number" ? row.value : Number(row.value ?? 0);
    case "lifecycle":
      return row.lifecycle_status;
    case "state":
      return row.stage_state;
    case "bucket":
      return row.qc?.bucket ?? "";
    case "status":
      return row.pipeline_status ?? "";
    default:
      return "";
  }
}

function pill(extra: string) {
  return `inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${extra}`;
}

function formatState(state: string) {
  return state.replace(/^staging\./, "").replace(/_/g, " ");
}

export function StagingTab() {
  const [dataset] = useActiveDataset();
  const adapter = useDataHubAdapter();
  const { data, loading, error } = useAdapterData(
    () => adapter.getStagingRows(),
    [dataset]
  );
  const [bucket, setBucket] = useState<BucketFilter>("all");
  const [field, setField] = useState<SearchField>("account");
  const [term, setTerm] = useState("");
  const [sortState, setSortState] = useState<SortState>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const rows = useMemo(() => {
    let out = data ?? [];
    if (bucket !== "all") {
      out = out.filter(r =>
        bucket === "needs_review"
          ? r.qc?.bucket === "open"
          : r.qc?.bucket === bucket
      );
    }
    if (term.trim()) {
      const t = term.trim().toLowerCase();
      out = out.filter(r => fieldValue(r, field).toLowerCase().includes(t));
    }
    if (sortState) {
      const key = sortState.key as SortKey;
      out = [...out].sort((a, b) => {
        const av = sortValue(a, key);
        const bv = sortValue(b, key);
        const cmp =
          typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av).localeCompare(String(bv));
        return sortState.order === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [data, bucket, field, term, sortState]);

  const columns = useMemo(
    () => [
      {
        key: "account",
        title: "Account",
        width: "200px",
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "account"),
        render: (_: unknown, r: StagingRow) => {
          const findings = r.qc?.findings ?? [];
          const open = expanded === r.id;
          const label = r.account_name || r.source_account_name || "—";
          if (!findings.length) {
            return <span className="font-medium text-gray-900">{label}</span>;
          }
          return (
            <button
              type="button"
              className="flex items-center gap-1.5 text-left font-medium text-gray-900 hover:text-purple-700"
              onClick={() => setExpanded(open ? null : r.id)}
            >
              {open ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              )}
              <span className="truncate">{label}</span>
            </button>
          );
        },
      },
      {
        key: "metric_id",
        title: "Metric ID",
        width: "140px",
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "metric_id"),
        render: (_: unknown, r: StagingRow) => (
          <span className="text-sm text-gray-700">{r.metric_id || "—"}</span>
        ),
      },
      {
        key: "metric_name",
        title: "Metric name",
        width: "180px",
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "metric_name"),
        render: (_: unknown, r: StagingRow) => (
          <span className="text-gray-700">{r.metric_name || "—"}</span>
        ),
      },
      {
        key: "date",
        title: "Date",
        width: "120px",
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "date"),
        render: (_: unknown, r: StagingRow) => (
          <span className="text-gray-600">{r.measurement_date || "—"}</span>
        ),
      },
      {
        key: "value",
        title: "Value",
        width: "100px",
        textalign: "left" as const,
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "value"),
        render: (_: unknown, r: StagingRow) => (
          <span className="tabular-nums text-gray-900">{r.value ?? "—"}</span>
        ),
      },
      {
        key: "lifecycle",
        title: "Lifecycle",
        width: "110px",
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "lifecycle"),
        render: (_: unknown, r: StagingRow) => (
          <span
            className={pill(
              r.lifecycle_status === "accepted"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            )}
          >
            {r.lifecycle_status}
          </span>
        ),
      },
      {
        key: "state",
        title: "State",
        width: "110px",
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "state"),
        render: (_: unknown, r: StagingRow) => (
          <span className="capitalize text-gray-600">
            {formatState(r.stage_state)}
          </span>
        ),
      },
      {
        key: "bucket",
        title: "Bucket",
        width: "110px",
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "bucket"),
        render: (_: unknown, r: StagingRow) => {
          const b = r.qc?.bucket;
          if (!b) return <span className="text-gray-400">—</span>;
          return (
            <span
              className={pill(BUCKET_PILL[b] || "bg-gray-100 text-gray-700")}
            >
              {b}
            </span>
          );
        },
      },
      {
        key: "status",
        title: "Pipeline status",
        width: "140px",
        sortable: true,
        sortValue: (r: StagingRow) => sortValue(r, "status"),
        render: (_: unknown, r: StagingRow) => {
          const findings = r.qc?.findings ?? [];
          const status = r.pipeline_status ?? "—";
          return (
            <span className="inline-flex items-center gap-1.5">
              <span
                className={pill(
                  status === "clean"
                    ? "bg-green-50 text-green-800"
                    : "bg-gray-100 text-gray-700"
                )}
              >
                {status}
              </span>
              {findings.length > 0 && (
                <span className={pill("bg-amber-100 text-amber-900")}>
                  {findings.length}
                </span>
              )}
            </span>
          );
        },
      },
    ],
    [expanded]
  );

  if (loading && !(data ?? []).length)
    return <TabLoading label="Loading staging records…" />;
  if (error) return <TabError error={error} />;

  const visible = rows.slice(0, 250);
  const expandedRowKeys = expanded ? [expanded] : [];

  return (
    <div>
      <div className="relative z-20 flex flex-col gap-3 border-b border-gray-200 px-4 py-3 sm:flex-row sm:flex-nowrap sm:items-center">
        <div className="min-w-0 shrink-0">
          <h3 className="m-0 text-base font-semibold text-gray-900">
            Staging records
          </h3>
          <p className="m-0 text-xs text-gray-500">
            {visible.length.toLocaleString()} of {rows.length.toLocaleString()}{" "}
            shown
          </p>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:ml-auto sm:flex-nowrap">
          <div className="min-w-[180px] flex-1 sm:w-[240px] sm:flex-none">
            <SearchInput
              value={term}
              onChange={e => setTerm(e.target.value)}
              placeholder={`Search by ${SEARCH_FIELDS.find(f => f.value === field)?.label.toLowerCase()}…`}
            />
          </div>
          <DropdownSelector
            label="Field"
            selected={field}
            options={SEARCH_FIELDS.map(f => ({
              label: f.label,
              value: f.value,
            }))}
            onSelect={value => setField(value as SearchField)}
          />
          <DropdownSelector
            label="Status"
            selected={bucket}
            options={BUCKET_FILTERS.map(b => ({
              label: BUCKET_LABELS[b],
              value: b,
            }))}
            onSelect={value => setBucket(value as BucketFilter)}
          />
        </div>
      </div>

      <div className="relative z-0 overflow-hidden">
        <Table
          className="[&_th]:normal-case [&_th]:tracking-normal"
          columns={columns}
          data={visible}
          loading={loading && !visible.length}
          rowKey={r => r.id}
          sortable
          sortState={sortState}
          onSortChange={setSortState}
          expandedRowKeys={expandedRowKeys}
          expandedRowRender={(r: StagingRow) => {
            const findings = r.qc?.findings ?? [];
            if (!findings.length) return null;
            return (
              <div>
                <p className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Why this row is {r.qc?.bucket ?? "flagged"}
                </p>
                <div className="space-y-1.5">
                  {findings.map((f, i) => (
                    <div
                      key={`${f.probe_id}-${i}`}
                      className="flex flex-wrap items-start gap-2 text-xs"
                    >
                      <span
                        className={pill(
                          f.severity === "hard"
                            ? "bg-red-100 text-red-800"
                            : "bg-amber-100 text-amber-900"
                        )}
                      >
                        {f.severity}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        {f.probe_id}
                      </span>
                      <span className="text-gray-700">{f.message}</span>
                    </div>
                  ))}
                </div>
                {findings.some(f => f.severity === "hard") && (
                  <p className="m-0 mt-2 text-[11px] text-gray-500">
                    A <strong>hard</strong> finding blocks the row from reaching
                    production. Warnings do not.
                  </p>
                )}
              </div>
            );
          }}
          emptyContent={
            <p className="m-0 text-sm text-gray-500">
              No {DATASET_LABELS[dataset].toLowerCase()} staging rows match this
              filter.
            </p>
          }
          scrollable
          maxHeight="calc(100vh - 28rem)"
        />
      </div>
    </div>
  );
}

export default StagingTab;
